import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArchiveRestore, Eye, RotateCcw, Send, Trash2, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ListPagination } from '@/components/efs/ListPagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/efs/PageHeader';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import { DispatchStatusBadge } from '../DispatchStatusBadge';
import { DeleteDispatchDialog } from '../DeleteDispatchDialog';
import { DeletedDispatchesDialog } from '../DeletedDispatchesDialog';
import { DispatchDateRangeFilter } from '../DispatchDateRangeFilter';
import { DispatchRequestPreviewSheet } from '../DispatchRequestPreviewSheet';
import { DispatchStopsSummary } from '../DispatchStopsSummary';
import { formatDispatchTime } from '../format';
import { RejectDispatchDialog } from '../RejectDispatchDialog';
import { ReturnToDraftDispatchDialog } from '../ReturnToDraftDispatchDialog';
import { RouteDispatchDialog } from './RouteDispatchDialog';
import { useSuppliersList } from '@/features/masters/suppliers/useSuppliers';
import {
  useDeleteDispatchChain,
  useDispatchDeletePreview,
  usePlatformRequests,
  useRejectPlatformRequest,
  useReturnDispatchRequestToDraft,
} from '../useDispatch';
import type { DispatchRequestDto, DispatchRequestStatus } from '../types';

const NS = 'operations';
const ALL = '__all__';
const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];
const PLATFORM_STATUS_FILTERS: Array<DispatchRequestStatus | typeof ALL> = [
  ALL,
  'submitted',
  'routed',
  'accepted',
  'fulfilled',
  'rejected',
  'cancelled',
];
const RETURN_TO_DRAFT_STATUSES = new Set<DispatchRequestStatus>([
  'submitted',
  'routed',
  'rejected',
  'cancelled',
]);

const isReturnToDraftEligible = (request: DispatchRequestDto) =>
  RETURN_TO_DRAFT_STATUSES.has(request.status) &&
  !request.accepted_at &&
  !request.accepted_by &&
  !request.linked_inbound_lot_id;

/**
 * Platform-admin dispatch inbox. Operators can inspect all statuses and only
 * act on rows that are still waiting for platform routing.
 */
interface PlatformInboxPageProps {
  embedded?: boolean;
}

export const PlatformInboxPage = ({ embedded = false }: PlatformInboxPageProps) => {
  const { t } = useTranslation(NS);
  const user = useCurrentUser();
  const [statusFilter, setStatusFilter] = useState<DispatchRequestStatus | typeof ALL>(ALL);
  const [requestedDateFrom, setRequestedDateFrom] = useState('');
  const [requestedDateTo, setRequestedDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const list = usePlatformRequests({
    page,
    per_page: perPage,
    ...(statusFilter === ALL ? {} : { status: statusFilter }),
    ...(requestedDateFrom ? { requested_date_from: requestedDateFrom } : {}),
    ...(requestedDateTo ? { requested_date_to: requestedDateTo } : {}),
  });
  const suppliersQuery = useSuppliersList({ active_only: false, per_page: 500 });
  const reject = useRejectPlatformRequest();
  const returnToDraft = useReturnDispatchRequestToDraft();
  const deleteDispatch = useDeleteDispatchChain();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [target, setTarget] = useState<DispatchRequestDto | null>(null);
  const [rejectTarget, setRejectTarget] = useState<DispatchRequestDto | null>(null);
  const [returnToDraftTarget, setReturnToDraftTarget] = useState<DispatchRequestDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DispatchRequestDto | null>(null);
  const [deletedOpen, setDeletedOpen] = useState(false);
  const deletePreview = useDispatchDeletePreview(deleteTarget?.dispatch_request_id, !!deleteTarget);
  const [previewRow, setPreviewRow] = useState<DispatchRequestDto | null>(null);

  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const meta = list.data?.meta;
  const total = meta?.total ?? rows.length;
  const pendingCount = useMemo(
    () => rows.filter((row) => row.status === 'submitted').length,
    [rows],
  );
  const canRoute = hasPermission(user, 'dispatch.request.route');
  const canReject = hasPermission(user, 'dispatch.request.reject_platform');
  const canDelete = hasPermission(user, 'dispatch.request.delete');
  const canReturnToDraft = canRoute;
  const supplierNameById = new Map(
    (suppliersQuery.data?.rows ?? []).map((supplier) => [
      supplier.company_id,
      supplier.display_name,
    ]),
  );

  const openRoute = (row: DispatchRequestDto) => {
    setTarget(row);
    setDialogOpen(true);
  };

  const onReject = (row: DispatchRequestDto) => {
    reject.reset();
    setRejectTarget(row);
  };

  const onReturnToDraft = (row: DispatchRequestDto) => {
    returnToDraft.reset();
    setReturnToDraftTarget(row);
  };

  return (
    <div className="space-y-4">
      {!embedded ? (
        <PageHeader
          title={t('efs.operations.dispatch.platform.title', {
            defaultValue: '配車ルーティング受信箱',
          })}
          subtitle={t('efs.operations.dispatch.platform.subtitle', {
            defaultValue: 'ルーティング待ちの配車・配送依頼',
          })}
          count={total}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {PLATFORM_STATUS_FILTERS.map((status) => (
          <Button
            key={status}
            type="button"
            size="sm"
            variant={statusFilter === status ? 'default' : 'outline'}
            onClick={() => {
              setPage(1);
              setStatusFilter(status);
            }}
          >
            {status === ALL
              ? t('efs.operations.dispatch.filters.all', { defaultValue: '全部' })
              : t(`efs.operations.dispatch.statuses.${status}`, { defaultValue: status })}
          </Button>
        ))}
        <span className="ml-auto text-xs text-efs-text-muted">
          {t('efs.operations.dispatch.platform.pending_count', {
            count: pendingCount,
            defaultValue: '本頁待指派 {{count}} 件',
          })}
        </span>
        {canDelete ? (
          <Button type="button" size="sm" variant="outline" onClick={() => setDeletedOpen(true)}>
            <ArchiveRestore className="mr-1 h-3 w-3" aria-hidden="true" />
            {t('efs.operations.dispatch.actions.recently_deleted', {
              defaultValue: '最近削除',
            })}
          </Button>
        ) : null}
      </div>

      <DispatchDateRangeFilter
        from={requestedDateFrom}
        to={requestedDateTo}
        onFromChange={(value) => {
          setPage(1);
          setRequestedDateFrom(value);
        }}
        onToChange={(value) => {
          setPage(1);
          setRequestedDateTo(value);
        }}
        onClear={() => {
          setPage(1);
          setRequestedDateFrom('');
          setRequestedDateTo('');
        }}
      />

      <div className="rounded-md border border-efs-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('efs.operations.dispatch.fields.type')}</TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.supplier')}</TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.trip_code')}</TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.requested_date')}</TableHead>
              <TableHead>
                {t('efs.operations.dispatch.fields.requested_time', {
                  defaultValue: '希望時間',
                })}
              </TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.location')}</TableHead>
              <TableHead>
                {t('efs.operations.dispatch.fields.status', { defaultValue: '狀態' })}
              </TableHead>
              <TableHead className="text-right">
                {t('efs.operations.dispatch.fields.actions', { defaultValue: '操作' })}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-efs-text-muted">
                  {t('efs.common.messages.loading', { ns: 'common' })}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-efs-text-muted">
                  {statusFilter === ALL
                    ? t('efs.operations.dispatch.platform.empty_all', {
                        defaultValue: '配車・配送依頼はありません',
                      })
                    : t('efs.operations.dispatch.platform.empty_filtered', {
                        status: t(`efs.operations.dispatch.statuses.${statusFilter}`, {
                          defaultValue: statusFilter,
                        }),
                        defaultValue: '{{status}} の配車・配送依頼はありません',
                      })}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.dispatch_request_id}>
                  <TableCell className="text-xs">
                    {t(`efs.operations.dispatch.types.${row.request_type}`, {
                      defaultValue: row.request_type,
                    })}
                  </TableCell>
                  <TableCell className="text-xs">
                    {supplierNameById.get(row.supplier_company_id) ??
                      `${row.supplier_company_id.slice(0, 8)}…`}
                  </TableCell>
                  <TableCell className="text-xs">
                    <span className="font-mono text-efs-text-muted">
                      {row.trip_code ?? row.temporary_code}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">{row.requested_date ?? '—'}</TableCell>
                  <TableCell className="text-xs">
                    {formatDispatchTime(row.requested_time)}
                  </TableCell>
                  <TableCell className="text-xs">
                    <DispatchStopsSummary row={row} />
                  </TableCell>
                  <TableCell>
                    <DispatchStatusBadge status={row.status} />
                  </TableCell>
                  <TableCell className="space-x-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setPreviewRow(row)}>
                      <Eye className="mr-1 h-3 w-3" aria-hidden="true" />
                      {t('efs.operations.dispatch.actions.preview', {
                        defaultValue: '檢視',
                      })}
                    </Button>
                    {canRoute ? (
                      <Button
                        size="sm"
                        variant="default"
                        disabled={row.status !== 'submitted'}
                        onClick={() => openRoute(row)}
                      >
                        <Send className="mr-1 h-3 w-3" />
                        {t('efs.operations.dispatch.actions.route', { defaultValue: '指派' })}
                      </Button>
                    ) : null}
                    {canReject ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reject.isPending || row.status !== 'submitted'}
                        onClick={() => onReject(row)}
                      >
                        <XCircle className="mr-1 h-3 w-3" />
                        {t('efs.operations.dispatch.actions.reject')}
                      </Button>
                    ) : null}
                    {canReturnToDraft && isReturnToDraftEligible(row) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={returnToDraft.isPending}
                        onClick={() => onReturnToDraft(row)}
                      >
                        <RotateCcw className="mr-1 h-3 w-3" />
                        {t('efs.operations.dispatch.actions.return_to_draft', {
                          defaultValue: '下書きに戻す',
                        })}
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={deleteDispatch.isPending}
                        onClick={() => {
                          deleteDispatch.reset();
                          setDeleteTarget(row);
                        }}
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        {t('efs.operations.dispatch.actions.delete_chain', {
                          defaultValue: '削除',
                        })}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ListPagination
        page={meta?.page ?? page}
        perPage={meta?.per_page ?? perPage}
        total={total}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPerPageChange={(nextPerPage) => {
          setPage(1);
          setPerPage(nextPerPage === 100 ? 100 : DEFAULT_PER_PAGE);
        }}
      />

      <RouteDispatchDialog open={dialogOpen} onOpenChange={setDialogOpen} request={target} />
      <DispatchRequestPreviewSheet
        open={!!previewRow}
        onOpenChange={(open) => {
          if (!open) setPreviewRow(null);
        }}
        request={previewRow}
        supplierName={previewRow ? supplierNameById.get(previewRow.supplier_company_id) : undefined}
      />
      <RejectDispatchDialog
        open={!!rejectTarget}
        onOpenChange={(open) => {
          if (!open) setRejectTarget(null);
        }}
        request={rejectTarget}
        isPending={reject.isPending}
        error={reject.error}
        onConfirm={(body) => {
          if (!rejectTarget) return;
          reject.mutate(
            { id: rejectTarget.dispatch_request_id, body },
            { onSuccess: () => setRejectTarget(null) },
          );
        }}
      />
      <ReturnToDraftDispatchDialog
        open={!!returnToDraftTarget}
        onOpenChange={(open) => {
          if (!open) setReturnToDraftTarget(null);
        }}
        request={returnToDraftTarget}
        isPending={returnToDraft.isPending}
        error={returnToDraft.error}
        onConfirm={(reason) => {
          if (!returnToDraftTarget) return;
          returnToDraft.mutate(
            { id: returnToDraftTarget.dispatch_request_id, body: { reason } },
            { onSuccess: () => setReturnToDraftTarget(null) },
          );
        }}
      />
      <DeleteDispatchDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        request={deleteTarget}
        preview={deletePreview.data ?? null}
        previewLoading={deletePreview.isLoading || deletePreview.isFetching}
        previewError={deletePreview.error}
        isPending={deleteDispatch.isPending}
        error={deleteDispatch.error}
        onConfirm={(reason) => {
          if (!deleteTarget) return;
          deleteDispatch.mutate(
            { id: deleteTarget.dispatch_request_id, reason },
            { onSuccess: () => setDeleteTarget(null) },
          );
        }}
      />
      <DeletedDispatchesDialog open={deletedOpen} onOpenChange={setDeletedOpen} />
    </div>
  );
};

export default PlatformInboxPage;
